import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { FaEyeSlash, FaEye } from "react-icons/fa";
import { AuthApis } from "../../../apis/authApis";
import { useSelector, useDispatch } from 'react-redux';
import { login } from '../../reducer/loginSlice';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ChevronDown } from 'lucide-react';
import LoadingSpinner from "../../UI/LoadingSpinner";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import configs from "../../../configs";

function Register() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [phone, setPhone] = useState<any>('');
  const [searchParams] = useSearchParams();
  const [refId, setRefId] = useState<string | null>(searchParams.get('ref'));
  const [promoId, setPromoId] = useState<string | null>(searchParams.get('promo'));
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [promoError, setPromoError] = useState('');
  const [referralError, setReferralError] = useState('');

  const userLoginData = useSelector((state: any) => state.data.login.value);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Set initial user data state including referral and promo codes
  const [userData, setUserdata] = useState({
    'email': "",
    'f_name': "",
    'l_name': "",
    'phone_number': "",
    'password': "",
    'confirm_password': "",
    'referral_code': refId || "",
    'promo_code': promoId || ""
  });

  // Update referral code if URL param changes
  useEffect(() => {
    if (refId) {
      setUserdata(prevState => ({
        ...prevState,
        'referral_code': refId
      }));
    }
  }, [refId]);

  // Update promo code if URL param changes
  useEffect(() => {
    if (promoId) {
      setUserdata(prevState => ({
        ...prevState,
        'promo_code': promoId
      }));
    }
  }, [promoId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setUserdata({ ...userData, [name]: value });

    if (name === 'promo_code') {
      setPromoError('');
    }
    if (name === 'referral_code') {
      setReferralError('');
    }

    // Password strength validation
    if (name === 'password') {
      const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[a-zA-Z\d!@#$%^&*()_+]{8,}$/;

      const isValidPassword = strongPasswordRegex.test(value);
      if (!isValidPassword) {
        setPasswordError('Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.');
      } else {
        setPasswordError('');
      }
    }

    // Confirm password match validation
    if (name === 'confirm_password') {
      if (value !== userData.password) {
        setPasswordError("Password and Confirm Password don't match.");
      } else {
        setPasswordError('');
      }
    }

    if (name === 'email') {
      // Regular expression for email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValidEmail = emailRegex.test(value);

      // Update the email error state based on validation result
      setEmailError(isValidEmail ? '' : 'Please enter a valid email address.');
    }
  };

  const handleInput = (event: any) => {
    const value = event.target.value;
    const regex = /^[a-zA-Z]*$/; // Regular expression to match alphabetic characters only

    if (!regex.test(value)) {
      event.target.value = value.replace(/[^a-zA-Z]/g, ''); // Remove any non-alphabetic characters
    }
  };

  const togglePasswordVisibility = (field: string) => {
    if (field === 'password') {
      setShowPassword(!showPassword);
    } else if (field === 'confirm_password') {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setPromoError('');
    setReferralError('');

    // Check if password and confirm password match
    if (userData.password !== userData.confirm_password) {
      toast.error("Password and Confirm Password must match.");
      setLoading(false);
      return; // Prevent form submission if passwords don't match
    }

    // While payments are off, an access code is required to sign up.
    if (!configs.paymentsEnabled && !userData.promo_code?.trim()) {
      setPromoError('An access code is required to create an account.');
      toast.error('An access code is required to create an account.');
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('email', userData.email);
    formData.append('first_name', userData.f_name);
    formData.append('last_name', userData.l_name);
    formData.append('phone_number', userData.phone_number);
    formData.append('password_confirmation', userData.confirm_password);
    formData.append('password', userData.password);

    // Add referral code if it exists
    if (userData.referral_code) {
      formData.append('referral_code', userData.referral_code);
    }

    // Add promo code if it exists (lifetime access)
    if (userData.promo_code) {
      formData.append('promo_code', userData.promo_code);
    }

    AuthApis.register(formData)
      .then((response) => {
        if (response?.data) {
          if (response?.data?.status === 'success') {
            dispatch(login({
              email: "",
              name: "",
              password: "",
              token: "",
              data: []
            }));
            toast.success(response?.data?.message);
            navigate('/email-verify');
          } else {
            toast.error(response?.data?.message);
          }
        }
      })
      .catch(function (error) {
        console.log(error.response?.data);
        const status = error.response?.status;
        const fieldErrors = error.response?.data?.errors;
        if (status === 422 && fieldErrors) {
          if (fieldErrors.promo_code?.[0]) {
            setPromoError(fieldErrors.promo_code[0]);
          }
          if (fieldErrors.referral_code?.[0]) {
            setReferralError(fieldErrors.referral_code[0]);
          }
        }
        toast.error(error.response?.data?.message || "Registration failed");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <section className="bg-[#F8F8F8] min-h-screen body-font font-poppins pb-20">
        <div className="bg-gray-100 py-10 pl-10">
          <a href="/">
            <img src="images/log.svg" alt="Logo" />
          </a>

        </div>
        <div className="flex flex-col md:flex-row">
          {/* Left Section - Images */}
          <div className="bg-gray-100 flex-1 hidden md:flex flex-col items-center justify-center p-4">
            <div className="grid grid-cols-2 gap-4 w-full max-w-md">
              <img
                src="images/img4.png"
                alt="Family 1"
                className="rounded-lg object-cover w-full h-full"
              />
              <img
                src="images/img5.png"
                alt="Family 2"
                className="rounded-lg object-cover w-full h-full"
              />
              <img
                src="images/img11.png"
                alt="Family 3"
                className="rounded-lg object-cover w-full h-full"
              />
              <img
                src="images/img2.png"
                alt="Family 4"
                className="rounded-lg object-cover w-full h-full"
              />
            </div>
          </div>

          {/* Right Section - Form */}
          <div className="flex-1 flex flex-col items-center justify-center p-6">
            <div className="max-w-md w-full">
              <h1 className="text-2xl font-bold mb-4 text-center">Create Your Account</h1>
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      name="f_name"
                      placeholder="First Name"
                      value={userData.f_name}
                      onChange={handleChange}
                      onInput={handleInput}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="l_name"
                      placeholder="Last Name"
                      value={userData.l_name}
                      onChange={handleChange}
                      onInput={handleInput}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={userData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {emailError && <p className="text-red-500 text-sm">{emailError}</p>}

                <div>
                  <input
                    type="number"
                    name="phone_number"
                    placeholder="Enter phone number (e.g 2348176599345)"
                    value={userData.phone_number}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      value={userData.password}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 pr-10 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => togglePasswordVisibility('password')}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirm_password"
                      placeholder="Confirm Password"
                      value={userData.confirm_password}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 pr-10 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => togglePasswordVisibility('confirm_password')}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                {passwordError && <p className="text-red-500 text-sm">{passwordError}</p>}

                {/* Referral Code Field */}
                <div>
                  <input
                    type="text"
                    name="referral_code"
                    placeholder="Referral Code (Optional)"
                    value={userData.referral_code}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {referralError && <p className="text-red-500 text-sm mt-1">{referralError}</p>}
                </div>

                {/* Access Code Field (grants free 1 TB lifetime access) */}
                <div>
                  <input
                    type="text"
                    name="promo_code"
                    placeholder={configs.paymentsEnabled ? "Promo Code (Optional)" : "Access Code (Required)"}
                    value={userData.promo_code}
                    onChange={handleChange}
                    required={!configs.paymentsEnabled}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {promoError && <p className="text-red-500 text-sm mt-1">{promoError}</p>}
                </div>

                <div className="flex items-center justify-between text-sm">
                  <p className="text-gray-600">
                    Already have an account?{' '}
                    <a href="/sign-in" className="text-blue-500 hover:underline">
                      Sign in
                    </a>
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-500 text-white py-3 px-4 rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  {loading ? 'Loading...' : 'Sign up'}
                </button>
              </form>
              <div className="mt-8">
                <p className="text-center text-sm text-gray-500">
                  &copy; 2023 bETERNAL. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ToastContainer
        position="bottom-left"
        autoClose={2000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
}

export default Register;